<template>
  <v-container class="pa-0">
    <div class="grid-options-layout px-2">
      <div v-if="!hasEssentials" class="grid-options-layout-entry">
        <v-checkbox
          v-model="eventsAiEnabled"
          label="Events AI"
          @change="handleEventsAIChange"
          hide-details
          dense
        />
      </div>
      <div class="grid-options-layout-entry">
        <v-checkbox
          v-model="analysisEnabled"
          label="Analysis"
          @change="handleAnalysisChange"
          hide-details
          dense
        />
      </div>
      <div class="grid-options-layout-entry">
        <v-checkbox
          v-model="verifiedEventsEnabled"
          label="Verified Events"
          @change="handleVEChange"
          hide-details
          dense
        />
      </div>
    </div>
    <Loader v-if="loading" text="" color="black" />
    <DetailsListCluster
      v-if="items.length > 0"
      theme="light"
      :items="items"
      :page="page"
      :pageSize="pageSize"
      :height-percent="100"
      :height-pixels="0"
      :show-primary-topics="true"
      :show-thumbs-down="true"
      :get-threshold-type-function="getThresholdTypeFunction"
      :get-live-search-params-function="getParams"
      :scroll-to-top="scrollToTop"
      :matomo-category="analyticsCategory"
      @get-data="getContent"
      @loaded="loading = false"
    />
    <h4 class="text-center" v-if="!loading && items.length === 0">
      No headlines available
    </h4>
  </v-container>
</template>

<script>
import lodashGet from "lodash/get";
import lodashIsEqual from "lodash/isEqual";
import lodashDebounce from "lodash/debounce";
import lodashCloneDeep from "lodash/cloneDeep";
import { getArticles } from "@/services/search/wod.service";
import { setInfiniteScrollState } from "@/common/basic.utilities";
import { mapGetters } from "vuex";
import { MODULE_PREFIX_USER } from "@/store";
import { SAVE_PREFERENCES } from "@/store/actions.type";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";
import { SOCIAL_SOURCES } from "@/common/constants/wodSocialSources";
import {
  hasTwitterAccess,
  hasEssentialsAccess,
} from "@/services/user/user.service";

export default {
  name: "Headlines",
  components: {
    DetailsListCluster: () =>
      import("@/components/common/details/DetailsListCluster"),
    Loader: () => import("@/components/common/Loader"),
  },
  props: {
    params: {
      type: Object,
    },
    analyticsCategory: {
      type: String,
      default: EVENT_CATEGORIES.headlines,
    },
  },
  data() {
    let _hasEssentials = hasEssentialsAccess();
    return {
      loading: false,
      eventsAiEnabled: !_hasEssentials,
      analysisEnabled: true,
      verifiedEventsEnabled: true,
      period: 0,
      regions: [],
      regionFilters: [],
      countryFilters: [],
      items: [],
      page: 1,
      pageSize: 25,
      timeout: null,
      scrollToTop: new Date().toISOString(),
      lastParams: null,
      hasEssentials: _hasEssentials,
      filterCols: Math.floor(_hasEssentials ? 12 / 4 : 12 / 5),
    };
  },
  computed: {
    ...mapGetters({
      preferences: MODULE_PREFIX_USER + "preferences",
    }),
  },
  mounted() {
    this.applyPrefs();
    this.initialize();
  },
  methods: {
    async initialize() {
      this.loading = true;

      this.getContent();
    },
    getContent(page, $state, params) {
      if (!page) {
        this.scrollToTop = new Date().toISOString();
      }
      this.loading = true;
      this.getContentDebounced(page, $state, params);
    },
    getContentDebounced: lodashDebounce(async function (page, $state, params) {
      let result;
      let dataParams;
      try {
        this.loading = true;

        dataParams = this.getParams(params) || {};

        page = !page ? 1 : page;
        const PAGE_OFFSET = (page - 1) * this.pageSize;

        dataParams.pageSize = this.pageSize;
        dataParams.pageOffset = PAGE_OFFSET;

        let currParams = lodashCloneDeep(dataParams);
        if (currParams.hasOwnProperty("pageOffset")) {
          delete currParams.pageOffset;
        }

        let prevParams = lodashCloneDeep(this.lastParams);
        if (prevParams && prevParams.hasOwnProperty("pageOffset")) {
          delete prevParams.pageOffset;
        }
        if (!lodashIsEqual(currParams, prevParams)) {
          this.lastTimestamp = new Date().toISOString();
        }

        this.lastParams = dataParams;

        if (page > 1) {
          this.handlePageTrackerEvent(
            this.analyticsCategory,
            EVENT_ACTIONS.scrolled,
            `Page ${page}`
          );
        }

        result = await getArticles(dataParams, this.lastTimestamp);
        if (
          result &&
          result.features &&
          this.lastTimestamp ===
            lodashGet(
              result,
              "features[0].properties.formatted.requestTimestamp"
            )
        ) {
          if (
            this.itemsResults &&
            Array.isArray(this.itemsResults) &&
            this.itemsResults.length &&
            page !== 1
          ) {
            this.itemsResults = this.itemsResults.concat(result.features);
          } else {
            this.itemsResults = result.features;
          }
          this.items = lodashCloneDeep(this.itemsResults);
        } else {
          this.itemsResults = null;
          this.featuresResults = null;
        }
      } catch (error) {
        console.error("Error in getContent: " + JSON.stringify(error));
        console.error(error);
      } finally {
        if (result) {
          setInfiniteScrollState(
            $state,
            { features: result.features },
            "features"
          );
        }
        this.page = page;
        this.loading = false;
        result = null;
      }
    }, 100),
    getParams() {
      let paramsCopy = lodashCloneDeep(this.params);
      paramsCopy.pageSize = this.pageSize;
      paramsCopy.pageOffset = this.page * this.pageOffset;
      paramsCopy.source = this.getSource();
      paramsCopy.sources = paramsCopy.source;
      paramsCopy.eventType = "all";
      return paramsCopy;
    },
    // copied from SearchContent
    getThresholdTypeFunction(doc) {
      let thresholdType = "cluster";
      if (
        lodashGet(doc, "properties.formatted.source") === "twitter.com" ||
        lodashGet(doc, "properties.formatted.source") === "twitter"
      ) {
        thresholdType = "twitter";
      }
      return thresholdType;
    },
    resetState() {
      this.lastTimestamp = null;

      this.page = 0;
      this.items = [];
    },
    getSource() {
      let sources = [];
      if (this.eventsAiEnabled) {
        sources.push("news");
        if (hasTwitterAccess()) {
          sources.push(SOCIAL_SOURCES);
        }
      }
      if (this.verifiedEventsEnabled) {
        sources.push("political");
        sources.push("maritime");
      }
      if (this.analysisEnabled) {
        sources.push("analysis");
      }

      // if nothing is selected, select all sources available
      if (sources.length === 0) {
        if (!this.hasEssentials) {
          if (hasTwitterAccess()) {
            sources.push(SOCIAL_SOURCES);
          }
          sources.push("news");
        }
        sources.push("political");
        sources.push("maritime");
        sources.push("analysis");
      }
      return sources.toString();
    },
    applyPrefs() {
      const latestPrefs = this.preferences?.latest || {};
      this.analysisEnabled = latestPrefs.analysisEnabled || false;
      this.verifiedEventsEnabled = latestPrefs.verifiedEventsEnabled || false;
      if (!this.hasEssentials) {
        this.eventsAiEnabled = latestPrefs.eventsAiEnabled || false;
      } else {
        this.eventsAiEnabled = false;
      }
    },
    setPrefs() {
      //Start with default values
      if (!this.preferences) {
        this.preferences = {};
      }
      if (!this.preferences.latest) {
        this.preferences.latest = {};
      }

      //Set the headlines prefs on the parent preferences object
      Object.assign(this.preferences.latest, {
        eventsAiEnabled: this.eventsAiEnabled,
        analysisEnabled: this.analysisEnabled,
        verifiedEventsEnabled: this.verifiedEventsEnabled,
      });

      //Dispatch the changes
      this.$store.dispatch(
        MODULE_PREFIX_USER + SAVE_PREFERENCES,
        this.preferences
      );
    },
    handleEventsAIChange(selected) {
      this.handlePageTrackerEvent(
        this.analyticsCategory,
        EVENT_ACTIONS.changed,
        `Content Type - Events AI - ${selected ? "on" : "off"}`
      );
    },
    handleAnalysisChange(selected) {
      this.handlePageTrackerEvent(
        this.analyticsCategory,
        EVENT_ACTIONS.changed,
        `Content Type - Analysis - ${selected ? "on" : "off"}`
      );
    },
    handleVEChange(selected) {
      this.handlePageTrackerEvent(
        this.analyticsCategory,
        EVENT_ACTIONS.changed,
        `Content Type - Verified Events - ${selected ? "on" : "off"}`
      );
    },
  },
  watch: {
    params: {
      async handler() {
        this.resetState();
        this.getContent();
      },
      deep: true,
    },
    eventsAiEnabled: {
      async handler() {
        this.setPrefs();
        this.resetState();
        this.getContent();
      },
      deep: true,
    },
    analysisEnabled: {
      async handler() {
        this.setPrefs();
        this.resetState();
        this.getContent();
      },
      deep: true,
    },
    verifiedEventsEnabled: {
      async handler() {
        this.setPrefs();
        this.resetState();
        this.getContent();
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
/* Content type checkboxes */
>>> .v-input--selection-controls {
  margin-top: 0;
}
>>> .v-input__slot .v-input--selection-controls__input {
  margin-bottom: auto;
}
.grid-options-layout-entry >>> .v-label {
  font-size: 13px;
}
>>> .v-input--selection-controls.v-input--dense
  .v-input--selection-controls__ripple {
  width: 18px;
  height: 18px;
  top: calc(50% - 16px);
  left: -8px;
}
>>> .v-input__slot .v-input--selection-controls__input {
  margin-bottom: auto;
  height: 15px;
  width: 15px;
  top: 2px;
}
>>> .v-input--selection-controls .v-icon.v-icon--dense {
  font-size: 15px;
}

.grid-options-layout {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, auto));
}
</style>
